/**
 * LRU缓存实现
 * 薄DDD方案 - 内存优化的缓存策略，防止内存泄漏
 */

interface LRUCacheItem<T> {
  key: string;
  value: T;
  timestamp: number;
  accessCount: number;
}

interface LRUCacheOptions {
  /** 最大容量 */
  maxSize: number;
  /** 过期时间（毫秒） */
  ttl?: number;
  /** 最大访问次数 */
  maxAccessCount?: number;
}

/**
 * LRU缓存类
 */
export class LRUCache<T = any> {
  private cache = new Map<string, LRUCacheItem<T>>();
  private options: Required<LRUCacheOptions>;
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(options: LRUCacheOptions) {
    this.options = {
      ttl: 0,
      maxAccessCount: 10000,
      ...options,
    };

    // 启动定期清理
    if (this.options.ttl > 0) {
      this.cleanupInterval = setInterval(
        () => this.cleanup(),
        this.options.ttl / 2,
      );
    }
  }

  /**
   * 获取缓存项
   */
  get(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    // 检查是否过期
    if (this.isExpired(item)) {
      this.cache.delete(key);
      return null;
    }

    // 更新访问信息
    item.accessCount++;
    item.timestamp = Date.now();

    // 移动到末尾（最近访问）
    this.cache.delete(key);
    this.cache.set(key, item);

    return item.value;
  }

  /**
   * 设置缓存项
   */
  set(key: string, value: T): void {
    // 如果已存在，更新值
    if (this.cache.has(key)) {
      const item = this.cache.get(key)!;
      item.value = value;
      item.timestamp = Date.now();
      item.accessCount++;
      return;
    }

    // 如果达到最大容量，删除最旧的项
    if (this.cache.size >= this.options.maxSize) {
      this.evictOldest();
    }

    // 添加新项
    this.cache.set(key, {
      key,
      value,
      timestamp: Date.now(),
      accessCount: 1,
    });
  }

  /**
   * 删除缓存项
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * 清空缓存
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * 检查键是否存在
   */
  has(key: string): boolean {
    return this.cache.has(key) && !this.isExpired(this.cache.get(key)!);
  }

  /**
   * 获取缓存大小
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * 获取缓存统计
   */
  getStats(): {
    size: number;
    maxSize: number;
    hitCount: number;
    missCount: number;
    hitRate: number;
    oldestKey?: string;
    oldestTimestamp?: number;
    newestKey?: string;
  } {
    let hitCount = 0;
    let missCount = 0;
    let oldestKey: string | undefined;
    let newestKey: string | undefined;

    this.cache.forEach((item) => {
      if (this.isExpired(item)) {
        missCount++;
      } else {
        hitCount++;
      }

      if (!oldestKey || item.timestamp < this.cache.get(oldestKey)!.timestamp) {
        oldestKey = item.key;
      }

      if (!newestKey || item.timestamp > this.cache.get(newestKey)!.timestamp) {
        newestKey = item.key;
      }
    });

    return {
      size: this.cache.size,
      maxSize: this.options.maxSize,
      hitCount,
      missCount,
      hitRate: hitCount + missCount > 0 ? hitCount / (hitCount + missCount) : 0,
      oldestKey,
      oldestTimestamp: oldestKey
        ? this.cache.get(oldestKey)?.timestamp
        : undefined,
      newestKey,
    };
  }

  /**
   * 获取所有键
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * 获取所有值
   */
  values(): T[] {
    const values: T[] = [];
    this.cache.forEach((item) => {
      if (!this.isExpired(item)) {
        values.push(item.value);
      }
    });
    return values;
  }

  /**
   * 遍历缓存
   */
  forEach(callback: (value: T, key: string) => void): void {
    this.cache.forEach((item, key) => {
      if (!this.isExpired(item)) {
        callback(item.value, key);
      }
    });
  }

  /**
   * 清理过期项
   */
  private cleanup(): void {
    const expiredKeys: string[] = [];

    this.cache.forEach((item, key) => {
      if (this.isExpired(item)) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach((key) => this.cache.delete(key));
  }

  /**
   * 删除最旧的项
   */
  private evictOldest(): void {
    let oldestKey: string | undefined;
    let oldestTimestamp = Infinity;

    this.cache.forEach((item, key) => {
      if (item.timestamp < oldestTimestamp) {
        oldestTimestamp = item.timestamp;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * 检查项是否过期
   */
  private isExpired(item: LRUCacheItem<T>): boolean {
    const now = Date.now();

    // 检查TTL
    if (this.options.ttl > 0 && now - item.timestamp > this.options.ttl) {
      return true;
    }

    // 检查最大访问次数
    if (
      this.options.maxAccessCount > 0 &&
      item.accessCount >= this.options.maxAccessCount
    ) {
      return true;
    }

    return false;
  }

  /**
   * 销毁缓存
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
  }
}

/**
 * 内存管理器
 */
export class MemoryManager {
  private caches = new Map<string, LRUCache<any>>();
  private maxMemoryUsage: number;
  private warningThreshold: number;

  constructor(
    maxMemoryUsage: number = 500 * 1024 * 1024,
    warningThreshold: number = 0.8,
  ) {
    this.maxMemoryUsage = maxMemoryUsage;
    this.warningThreshold = warningThreshold;
  }

  /**
   * 创建LRU缓存
   */
  createCache<T>(name: string, options: LRUCacheOptions): LRUCache<T> {
    const cache = new LRUCache<T>(options);
    this.caches.set(name, cache);
    this.checkMemoryUsage();
    return cache;
  }

  /**
   * 获取缓存
   */
  getCache<T>(name: string): LRUCache<T> | null {
    return this.caches.get(name) || null;
  }

  /**
   * 删除缓存
   */
  deleteCache(name: string): boolean {
    const cache = this.caches.get(name);
    if (cache) {
      cache.destroy();
      this.caches.delete(name);
      return true;
    }
    return false;
  }

  /**
   * 检查内存使用
   */
  private checkMemoryUsage(): void {
    if ("memory" in performance) {
      const memory = (performance as any).memory;
      const used = memory.usedJSHeapSize;
      const ratio = used / this.maxMemoryUsage;

      if (ratio > this.warningThreshold) {
        console.warn(`内存使用率过高: ${(ratio * 100).toFixed(2)}%`);

        // 清理最旧的缓存
        this.cleanupOldestCache();
      }
    }
  }

  /**
   * 清理最旧的缓存
   */
  private cleanupOldestCache(): void {
    let oldestKey: string | undefined;
    let oldestTime = Infinity;

    this.caches.forEach((cache, key) => {
      const stats = cache.getStats();
      if (stats.oldestTimestamp != null && stats.oldestTimestamp < oldestTime) {
        oldestTime = stats.oldestTimestamp;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.deleteCache(oldestKey);
    }
  }

  /**
   * 清理所有缓存
   */
  cleanupAll(): void {
    this.caches.forEach((cache) => cache.destroy());
    this.caches.clear();
  }
}

/**
 * 内存管理实例
 */
export const memoryManager = new MemoryManager();

/**
 * 创建带内存管理的缓存
 */
export function createManagedCache<T>(
  name: string,
  maxSize: number = 1000,
  ttl?: number,
): LRUCache<T> {
  return memoryManager.createCache(name, { maxSize, ttl });
}
